import { useEffect, useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import type { ServicePlan } from '@app/types';
import { Banner, Button, Field, Input, Modal } from '@app/components/ui';
import { longDate, time12 } from '@app/lib/format';

interface RescheduleServiceDialogProps {
  isOpen: boolean;
  plan: ServicePlan | null;
  /** Called with the new ISO date when the user confirms. */
  onConfirm: (newDateIso: string) => Promise<void> | void;
  onClose: () => void;
}

const pad = (n: number) => n.toString().padStart(2, '0');
/** Local-time string for <input type="datetime-local">. */
const toLocalInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/**
 * Move a service to a new date.
 *
 * - Past plans are explained as a copy: the original stays as history and a
 *   new plan is made with confirmations and practice progress reset and the
 *   setlist unpublished.
 * - Upcoming plans move in place; only team confirmations reset.
 *
 * The cascade rules live on the backend (POST
 * /api/organizations/churches/:cid/plans/:pid/reschedule); this only collects
 * the date and says what will happen. The date field is the browser's own
 * date-and-time control.
 */
export function RescheduleServiceDialog({ isOpen, plan, onConfirm, onClose }: RescheduleServiceDialogProps) {
  const [newDate, setNewDate] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPast = useMemo(() => (plan?.date ? new Date(plan.date).getTime() < Date.now() : false), [plan?.date]);

  // Five minutes of slack so filling the form doesn't trip the server's
  // "must be in the future" check.
  const minDateTime = useMemo(() => toLocalInput(new Date(Date.now() + 5 * 60 * 1000)), []);

  // Suggest the same time of day next week (or an hour from now).
  useEffect(() => {
    if (!isOpen || !plan?.date) {
      setNewDate('');
      setError(null);
      return;
    }
    const original = new Date(plan.date);
    const suggested = new Date();
    suggested.setDate(suggested.getDate() + 7);
    suggested.setHours(original.getHours(), original.getMinutes(), 0, 0);
    if (suggested.getTime() < Date.now()) suggested.setTime(Date.now() + 60 * 60 * 1000);
    setNewDate(toLocalInput(suggested));
    setError(null);
  }, [isOpen, plan?.date]);

  if (!isOpen || !plan) return null;

  const handleConfirm = async () => {
    if (!newDate) return setError('Please choose a new date.');
    const parsed = new Date(newDate);
    if (Number.isNaN(parsed.getTime())) return setError('That date is not valid.');
    if (parsed.getTime() <= Date.now()) return setError('New service date must be in the future.');
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(parsed.toISOString());
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to reschedule.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Reschedule service" width={460} dismissible={!submitting}>
      <div className="flex flex-col gap-4" data-testid="reschedule-dialog">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-[12px] bg-info-bg text-info flex items-center justify-center shrink-0" aria-hidden="true">
            <CalendarClock size={19} />
          </span>
          <p className="text-[14px] text-t3 leading-[1.55]">
            <span className="font-semibold text-t1">{plan.title || 'This service'}</span> is on {longDate(plan.date)} at {time12(plan.date)}.
          </p>
        </div>

        {isPast ? (
          <Banner tone="amb" role="status">
            <span data-testid="reschedule-past-warning">
              This service has already happened. We&apos;ll make a new plan for the new date with the same songs and team. Practice progress and confirmations
              reset, and the setlist stays unpublished until you publish it again.
            </span>
          </Banner>
        ) : (
          <Banner tone="info" role="status">
            <span data-testid="reschedule-future-warning">
              Moving it resets everyone&apos;s confirmation to <strong>Pending</strong> so they can confirm the new date.
            </span>
          </Banner>
        )}

        <Field label="New date and time" htmlFor="reschedule-date-input" error={error ? <span data-testid="reschedule-error">{error}</span> : undefined}>
          <Input
            id="reschedule-date-input"
            data-testid="reschedule-date-input"
            type="datetime-local"
            value={newDate}
            min={minDateTime}
            invalid={!!error}
            onChange={(e) => {
              setNewDate(e.target.value);
              setError(null);
            }}
          />
        </Field>

        <div className="flex gap-2.5 mt-1.5">
          <Button variant="ghost" shape="form" size="lg" onClick={onClose} disabled={submitting} data-testid="reschedule-cancel-button">
            Cancel
          </Button>
          <Button shape="form" size="lg" className="flex-1" loading={submitting} disabled={!newDate} onClick={handleConfirm} data-testid="reschedule-confirm-button">
            {submitting ? 'Rescheduling…' : 'Reschedule service'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
